import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, Link, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { DekoxIcon, ImageCountBadge, ImageGalleryModal } from "src/components";
import dummyPicture from "src/assets/images/picture.png";
import { hasValue, hasValueAndNotEmpty } from "@itk/typescript-utils";
import { Chip, IconButton, Tooltip } from "@itk/mui-components";
import pdfIcon from "src/assets/images/pdf.svg";
import documentIcon from "src/assets/images/document.svg";
import parse from "html-react-parser";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { useProjectsPageProductDetailsSectionT } from "src/translation-hooks/projects-page-product-details-section";
import { useProductInfo } from "src/pages/product-details/useProductInfo";
import { useUserSettings } from "src/hooks";
import { applicationActions } from "src/components/application-store";
import { connect } from "react-redux";
import { baseUrl, getFileUrl } from "src/utils";
import { fileDownloadFromUrl } from "src/utils/file-utils";

const ownActions = {
  showFile: applicationActions.showFile
};

type Props = typeof ownActions & {
  productId: string;
  onClose: () => void;
};

const ProductDetailsDrawerBase = (props: Props) => {
  const { productId, onClose, showFile } = props;
  const theme = useTheme();
  const { language } = useUserSettings();

  const { assetStoreData, groupedAssetStoreDocumentsByType, isLoadingImages, primaryPicture, pictures, currentIndex, product, setCurrentIndex } = useProductInfo({ productId });
  const projectsPageProductDetailsSectionT = useProjectsPageProductDetailsSectionT();

  const [open, setOpen] = useState(false);

  const handleOpen = (productPictureId: string) => {
    if (productPictureId) {
      setOpen(true);
      const index = pictures.findIndex(x => x.productPictureId === productPictureId);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  };

  if (!product)
    return (
      <Stack gap={2} px={2.5} py={2}>
        <Skeleton height={200} />
        <Skeleton height={40} />
        <Skeleton height={30} />
        <Skeleton height={30} />
        <Skeleton height={30} />
      </Stack>
    );

  return (
    <>
      <Stack px={2.5} py={2} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color={theme.palette.customColors.darkGrey}>
          {product.description}
        </Typography>
        <IconButton k={genericTranslationsK.close} onClick={onClose} size="small" sx={{ color: theme.palette.customColors.darkGrey }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider />

      <Stack px={2.5} py={2} overflow="auto" flex={1} gap={3}>
        {isLoadingImages ? (
          <Box>
            <Skeleton height={200} />
          </Box>
        ) : (
          <Box sx={{ cursor: pictures.length > 0 ? "pointer" : "not-allowed", position: "relative" }}>
            {pictures.length > 0 && (
              <ImageCountBadge>
                <DekoxIcon icon="imageOutlined" sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontSize: 14 }}>
                  {pictures.length}
                </Typography>
              </ImageCountBadge>
            )}

            <img
              src={primaryPicture ? primaryPicture.picture : dummyPicture}
              alt="primary product picture"
              width="100%"
              height={200}
              onClick={() => {
                handleOpen(primaryPicture?.productPictureId || pictures?.[0]?.productPictureId);
              }}
              style={{ objectFit: "cover" }}
            />

            <Grid container spacing={1}>
              {pictures
                .filter(picture => !picture.isPrimary)
                .slice(0, 3)
                .map(picture => {
                  return (
                    <Grid size={4} key={picture.picture}>
                      <img
                        src={picture.picture}
                        alt={`Product ${picture.productPictureId}`}
                        width="100%"
                        height={80}
                        onClick={() => handleOpen(picture.productPictureId)}
                        style={{ objectFit: "cover" }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          </Box>
        )}

        <Stack gap={3}>
          {product.description2 && (
            <Typography variant="h3" color="customColors.darkGrey">
              {product.description2}
            </Typography>
          )}

          {/* Product number */}
          {hasValue(product.productNumber) && (
            <Stack gap={1} direction="row">
              <DekoxIcon icon="numbers" sx={{ color: theme.palette.customColors.darkGrey }} />
              <Stack>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.productNumber}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey">
                  {product.productNumber}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Product type */}
          {product.productTypeName && (
            <Stack gap={1} direction="row">
              <DekoxIcon icon="category" sx={{ color: theme.palette.customColors.darkGrey }} />
              <Stack>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.productCategory}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey">
                  {product.productTypeName}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Product summary */}
          {product.summary && (
            <Stack gap={1} direction="row">
              <DekoxIcon icon="info" sx={{ color: theme.palette.customColors.darkGrey }} />
              <Stack>
                <Typography variant="body2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.productDescription}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey" lineHeight={1.5}>
                  {parse(product.summary)}
                </Typography>
              </Stack>
            </Stack>
          )}

          {hasValueAndNotEmpty(assetStoreData.text) && (
            <Accordion sx={{ "&.Mui-expanded": { margin: 0 }, backgroundColor: "transparent", boxShadow: "none", ":before": { display: "none" } }}>
              <AccordionSummary
                sx={{
                  padding: 0,
                  minHeight: 0,
                  "& .MuiAccordionSummary-content, &.MuiAccordionSummary-root .Mui-expanded": { margin: 0 },
                  "&.Mui-expanded": { minHeight: 0, marginBottom: 2 }
                }}
                expandIcon={<DekoxIcon icon="arrow-down" />}
              >
                <DekoxIcon icon="abcOutlined" sx={{ color: theme.palette.customColors.darkGrey, mr: 1.6 }} />
                <Typography variant="body2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.information}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ ml: 4, padding: 0, gap: 1 }}>
                {assetStoreData.text.map(assetStoreText => {
                  return (
                    <Typography key={assetStoreText.assetStoreId} variant="subtitle1" color="customColors.darkGrey" lineHeight={1.5}>
                      {parse(assetStoreText.text)}
                    </Typography>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          )}

          {hasValueAndNotEmpty(assetStoreData.links) && (
            <Accordion sx={{ "&.Mui-expanded": { margin: 0 }, backgroundColor: "transparent", boxShadow: "none", ":before": { display: "none" } }}>
              <AccordionSummary
                sx={{ padding: 0, minHeight: 0, "& .MuiAccordionSummary-content, &.MuiAccordionSummary-root .Mui-expanded": { margin: 0 }, "&.Mui-expanded": { minHeight: 0, marginBottom: 2 } }}
                expandIcon={<DekoxIcon icon="arrow-down" />}
              >
                <DekoxIcon icon="link" sx={{ color: theme.palette.customColors.darkGrey, mr: 1.6 }} />
                <Typography variant="body2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.links}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0, ml: 4 }}>
                <Stack gap={1}>
                  {assetStoreData.links.map(assetStoreLink => {
                    return (
                      <Link key={assetStoreLink.assetStoreId} target="_blank" href={assetStoreLink.url}>
                        {assetStoreLink.description}
                      </Link>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {hasValueAndNotEmpty(assetStoreData.documents) && (
            <Accordion sx={{ "&.Mui-expanded": { margin: 0 }, backgroundColor: "transparent", boxShadow: "none", ":before": { display: "none" } }}>
              <AccordionSummary
                sx={{
                  padding: 0,
                  minHeight: 0,
                  "& .MuiAccordionSummary-content, &.MuiAccordionSummary-root .Mui-expanded": { margin: 0 },
                  "&.Mui-expanded": { minHeight: 0, marginBottom: 2 }
                }}
                expandIcon={<DekoxIcon icon="arrow-down" />}
              >
                <DekoxIcon icon="descriptionOutlined" sx={{ color: theme.palette.customColors.darkGrey, mr: 1.6 }} />
                <Typography variant="body2" color="customColors.darkGrey">
                  {projectsPageProductDetailsSectionT.documents}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0, ml: 4 }}>
                <Stack gap={2}>
                  {Object.values(groupedAssetStoreDocumentsByType).map((groupedDocuments, index) => {
                    return (
                      <Stack key={index}>
                        <Box mb={1}>
                          <Chip
                            label={
                              groupedDocuments[0].documentType?.descriptionDK
                                ? language === "en"
                                  ? groupedDocuments[0].documentType?.descriptionUK
                                  : groupedDocuments[0].documentType?.descriptionDK
                                : projectsPageProductDetailsSectionT.otherDocuments
                            }
                            variant="filled"
                            size="small"
                          />
                        </Box>
                        <Stack flexWrap="wrap" direction="row" gap={1}>
                          {groupedDocuments.map(assetStoreDocument => {
                            const isPdf = assetStoreDocument.fileType.toLowerCase() === "pdf";
                            return (
                              <Stack
                                key={assetStoreDocument.assetStoreId}
                                alignItems="center"
                                sx={{ cursor: "pointer" }}
                                width={130}
                                bgcolor={theme.palette.customColors.white}
                                borderRadius={2}
                                onClick={() => {
                                  if (!assetStoreDocument.fileId) return;
                                  if (isPdf) {
                                    showFile({ fileId: assetStoreDocument.fileId });
                                  } else {
                                    fileDownloadFromUrl(baseUrl + getFileUrl(assetStoreDocument.fileId), assetStoreDocument.fileName);
                                  }
                                }}
                              >
                                <Stack justifyContent="center" alignItems="center" width="100%" padding={1} borderRadius={2} position="relative">
                                  <img src={isPdf ? pdfIcon : documentIcon} width="50%" />
                                  <Tooltip title={assetStoreDocument.description}>
                                    <DekoxIcon icon="info" fontSize="small" sx={{ position: "absolute", right: 1, top: 1 }} />
                                  </Tooltip>
                                </Stack>
                                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.4}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: "3", WebkitBoxOrient: "vertical" }}
                                  >
                                    {assetStoreDocument.fileName}.{assetStoreDocument.fileType || ""}
                                  </Typography>
                                </Stack>
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </Stack>

      {/* Gallery Modal  */}
      <ImageGalleryModal open={open} images={pictures.map(picture => picture.picture)} currentIndex={currentIndex} setOpen={setOpen} setCurrentIndex={setCurrentIndex} />
    </>
  );
};

export const ProductDetailsDrawer = connect(null, ownActions)(ProductDetailsDrawerBase);
